import {TranslationLang} from 'types';

type Named = {name: string};
type Descriptions =
	| Array<{lang: TranslationLang; translation: string}>
	| null
	| undefined;
type ExportVexCluster = Named & {
	id?: string;
	descriptions?: Descriptions;
	approvers?: Named[] | null;
};

export type VkoVexExportItem = Named & {
	descriptions?: Descriptions;
	regulationClusters: Named[];
	vkos: Named[];
	vexClusters: ExportVexCluster[];
	vexes: Named[];
};

const SEPARATOR = ';\n';

/**
 * Description in the UI language: English for an English UI, otherwise German,
 * falling back to any other language.
 */
export const pickDescription = (
	descriptions: Descriptions,
	language: string,
): string => {
	const filled = (descriptions ?? []).filter(x => x.translation?.trim());
	const preferred = language.toLowerCase().startsWith('en')
		? TranslationLang.En
		: TranslationLang.De;
	return (
		(filled.find(x => x.lang === preferred) ?? filled[0])?.translation ?? ''
	);
};

const joinNames = (items: Named[] | null | undefined, separator = SEPARATOR) =>
	(items ?? []).map(x => x.name).join(separator);

/** One line per VEX cluster ("Cluster: value"), so values stay matched to their cluster. */
const joinPerVexCluster = (
	vexClusters: ExportVexCluster[],
	getValue: (vexCluster: ExportVexCluster) => string,
) =>
	vexClusters
		.map(vexCluster => ({name: vexCluster.name, value: getValue(vexCluster)}))
		.filter(x => x.value)
		.map(x => `${x.name}: ${x.value}`)
		.join(SEPARATOR);

/**
 * Cells in export column order: Keywords | Description | RegulationClusters | Vkos |
 * VexClusters | Vex | VexCluster approvers
 */
export const buildVkoVexRow = (
	item: VkoVexExportItem,
	language: string,
): string[] => [
	item.name,
	pickDescription(item.descriptions, language),
	joinNames(item.regulationClusters),
	joinNames(item.vkos),
	joinNames(item.vexClusters),
	joinNames(item.vexes),
	joinPerVexCluster(item.vexClusters, vexCluster =>
		joinNames(vexCluster.approvers, ', '),
	),
];

/** One row per exported cluster, shared across all exported keywords. */
export const buildVexClusterDescriptionRows = (
	items: VkoVexExportItem[],
	language: string,
): string[][] => {
	const clusters = new Map<string, ExportVexCluster>();
	items.forEach(item =>
		item.vexClusters.forEach(cluster => {
			clusters.set(cluster.id ?? cluster.name, cluster);
		}),
	);
	return [...clusters.values()]
		.sort((a, b) => a.name.localeCompare(b.name))
		.map(cluster => [
			cluster.name,
			pickDescription(cluster.descriptions, language),
		]);
};
